/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import {
	Button,
	ListTable,
	Pager,
	Select,
	Typography,
} from 'design_system/src';
import { getUser } from 'localStorage/authStorage';
import { findAllOzonios } from 'models/ozonio/controllers/ozonios.controller';
import { Ozonio } from 'models/ozonio/interfaces/ozonio.interface';
import { fetchConversationsTable } from 'models/conversation/controllers/conversation.controller';
import { ReactComponent as Redirect } from 'design_system/src/static/icons/external-link.svg';
import {
	Conversation,
	ConversationResponderEnum,
	ConversationResponderValues,
	ConversationTableRow,
} from 'models/conversation/interfaces/conversation.interface';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import SearchBar from 'components/hocs/searchBar/searchBar';
import InfobipConversation from 'views/ozoner/details/conversation/InfobipConversation';

const INFOBIP_CONVERSATION_URL =
	'https://portal.infobip.com/conversations/all-work/preview?conversationId=';

const activeOptions = [
	{ value: '', label: 'Estado de la conversación' },
	{ value: 'true', label: 'Activas' },
	{ value: 'false', label: 'Inactivas' },
];

const responderOptions = [
	{ value: '', label: 'Responde' },
	...Object.values(ConversationResponderEnum).map((value) => ({
		value,
		label: ConversationResponderValues[value],
	})),
];

const Conversations = () => {
	const [data, setData] = useState<ConversationTableRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [query, setQuery] = useState('');
	const [phone, setPhone] = useState('');
	const [sellerFilter, setSellerFilter] = useState('');
	const [activeFilter, setActiveFilter] = useState('');
	const [responderFilter, setResponderFilter] = useState<
		ConversationResponderEnum | ''
	>('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pages, setPages] = useState(1);
	const [count, setCount] = useState(0);
	const [sellerOptions, setSellerOptions] = useState<
		{ value: string; label: string }[]
	>([]);
	const [sellersById, setSellersById] = useState<{ [id: string]: Ozonio }>({});
	const [sellersByInfobipId, setSellersByInfobipId] = useState<{
		[infobipId: string]: Ozonio;
	}>({});
	const [selectedConversation, setSelectedConversation] =
		useState<ConversationTableRow | null>(null);

	useEffect(() => {
		const user = getUser();
		findAllOzonios(user?.company._id ?? '', '').then((ozons) => {
			const byId: { [id: string]: Ozonio } = {};
			const byInfobipId: { [infobipId: string]: Ozonio } = {};
			const options = ozons.data
				.filter((ozonio) => ozonio.isActive)
				.map((ozonio) => {
					byId[ozonio._id] = ozonio;
					if (ozonio.infobipId) byInfobipId[ozonio.infobipId] = ozonio;
					return { value: ozonio._id, label: ozonio.name };
				})
				.sort((a, b) => a.label.localeCompare(b.label));
			setSellerOptions(options);
			setSellersById(byId);
			setSellersByInfobipId(byInfobipId);
		});
	}, []);

	const fetchTable = (page: number) => {
		setLoading(true);
		const seller = sellerFilter ? sellersById[sellerFilter] : undefined;
		fetchConversationsTable({
			phone: phone || undefined,
			conversationId: phone || undefined,
			isActive: activeFilter === '' ? undefined : activeFilter === 'true',
			sellerId: sellerFilter || undefined,
			sellerInfobipId: seller?.infobipId || undefined,
			responder: responderFilter || undefined,
			page,
		})
			.then((response) => {
				setData(response.data);
				setPages(response.pages);
				setCount(response.count);
			})
			.catch(commonErrorHandler)
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		setCurrentPage(1);
		fetchTable(1);
	}, [sellerFilter, activeFilter, responderFilter, phone]);

	const handleSearch = (event: React.FormEvent) => {
		event.preventDefault();
		setPhone(query);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		fetchTable(page);
	};

	const handleConversationUpdate = (updated: Conversation) => {
		setData((prev) =>
			prev.map((item) =>
				item._id === updated._id ? { ...item, ...updated } : item,
			),
		);
	};

	const resolveSellerName = (row: ConversationTableRow) => {
		if (row.sellerId && sellersById[row.sellerId]) {
			return sellersById[row.sellerId].name;
		}
		if (row.infobipAgentId && sellersByInfobipId[row.infobipAgentId]) {
			return sellersByInfobipId[row.infobipAgentId].name;
		}
		return 'Sin vendedor';
	};

	return (
		<div className="dim_100_per flex_content_col shadow_hard inner_container bg_neutral_400">
			<div className="flex_header">
				<div className="bg_neutral_0 shadow_hard p_t_xs p_b_md p_x_lg">
					<div className="display_flex flex_gap_sm flex_align_center">
						<Typography scale="heading3" weight="600" textColor="neutral_900">
							Conversaciones
						</Typography>
						<SearchBar
							className="flex_grow_1"
							handleSearch={handleSearch}
							query={query}
							setQuery={setQuery}
						/>
					</div>
					<div className="display_flex flex_gap_xl">
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Asignado"
							options={[{ value: '', label: 'Asignado' }, ...sellerOptions]}
							value={sellerFilter}
							onChange={(e: { target: { value: string } }) =>
								setSellerFilter(e.target.value)
							}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Activa"
							options={activeOptions}
							value={activeFilter}
							onChange={(e: { target: { value: string } }) =>
								setActiveFilter(e.target.value)
							}
						/>
						<Select
							fixedPlaceholder
							variant="default"
							placeholder="Responde"
							options={responderOptions}
							value={responderFilter}
							onChange={(e: { target: { value: string } }) =>
								setResponderFilter(e.target.value as ConversationResponderEnum)
							}
						/>
					</div>
				</div>
			</div>
			<div className="flex_body p_x_lg overflow_x_hidden">
				{loading ? (
					<Typography scale="medium" weight="400" className="p_y_lg">
						Cargando conversaciones...
					</Typography>
				) : data.length ? (
					<ListTable
						data={data}
						hideHeader={false}
						className="m_t_md"
						rowClassName="cursor_pointer"
						onRowClick={(row: ConversationTableRow) =>
							setSelectedConversation(row)
						}
						cols={[
							{
								label: 'ID',
								render: (row: ConversationTableRow) => (
									<Typography scale="medium" weight="400">
										{row._id}
									</Typography>
								),
							},
							{
								label: 'Teléfono',
								render: (row: ConversationTableRow) => (
									<Typography scale="medium" weight="400">
										{row.dialCode}
										{row.phone}
									</Typography>
								),
							},
							{
								label: 'Conversación',
								render: (row: ConversationTableRow) => (
									<a
										href={`${INFOBIP_CONVERSATION_URL}${row.conversationId}`}
										target="_blank"
										rel="noreferrer"
									>
										<Button scale="small" variant="outline" icon={<Redirect />}>
											Ver en Infobip
										</Button>
									</a>
								),
							},
							{
								label: 'Activa',
								render: (row: ConversationTableRow) => (
									<Typography
										scale="xsmall"
										weight="600"
										className={`p_x_md p_y_xxs br_xs text_center text_neutral_0 ${
											row.isActive ? 'bg_primary_300' : 'bg_neutral_600'
										}`}
									>
										{row.isActive ? 'Activa' : 'Inactiva'}
									</Typography>
								),
							},
							{
								label: 'Responde',
								render: (row: ConversationTableRow) => (
									<Typography scale="medium" weight="400">
										{ConversationResponderValues[row.responder]}
									</Typography>
								),
							},
							{
								label: 'Asignado',
								render: (row: ConversationTableRow) => (
									<Typography scale="medium" weight="400">
										{resolveSellerName(row)}
									</Typography>
								),
							},
						]}
					/>
				) : (
					<Typography scale="medium" weight="400" className="p_y_lg">
						No hay conversaciones para mostrar
					</Typography>
				)}
			</div>
			<div className="flex_footer p_x_lg p_y_sm bg_neutral_0 display_flex flex_justify_end">
				<Pager
					page={currentPage}
					totalPages={pages || 1}
					setPage={handlePageChange}
					count={count}
				/>
			</div>
			{selectedConversation && (
				<InfobipConversation
					open={!!selectedConversation}
					setOpen={() => setSelectedConversation(null)}
					conversation={selectedConversation}
					onUpdate={handleConversationUpdate}
				/>
			)}
		</div>
	);
};

export default Conversations;
